import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff } from "lucide-react";
import logoVyra from "@/assets/logo-vyra.png";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('[Login] Iniciando autenticação...', { email, isLogin });
    
    if (!email || !password) {
      toast({
        title: "Campos obrigatórios",
        description: "Por favor, preencha email e senha.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      if (isLogin) {
        console.log('[Login] Tentando login...');
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          console.error('[Login] Erro no login:', error);
          throw error;
        }

        console.log('[Login] ✅ Login bem-sucedido:', { 
          userId: data.user?.id,
          hasSession: !!data.session 
        });

        toast({
          title: "Bem-vindo de volta!",
          description: "Login realizado com sucesso.",
        });
        
        // Aguardar um frame antes de navegar para garantir que o estado foi atualizado
        setTimeout(() => {
          console.log('[Login] Navegando para /today');
          navigate("/today");
        }, 100);
      } else {
        const redirectUrl = `${window.location.origin}/`;
        
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: redirectUrl,
            data: {
              name: name || email.split('@')[0],
            }
          }
        });

        if (error) throw error;

        toast({
          title: "Conta criada!",
          description: "Você já pode começar a usar a VYRA.",
        });
        navigate("/onboarding");
      }
    } catch (error: any) {
      console.error('[Login] Erro na autenticação:', error);
      toast({
        title: "Erro na autenticação",
        description: error.message || "Ocorreu um erro. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-md p-8">
        {/* Logo VYRA */}
        <div className="flex justify-center mb-6 animate-fade-in">
          <img 
            src={logoVyra} 
            alt="VYRA" 
            className="h-6 opacity-40 hover:opacity-60 transition-opacity duration-300"
          />
        </div>
        
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">
            {isLogin ? "Entrar" : "Criar conta"}
          </h1>
          <p className="text-muted-foreground">
            {isLogin 
              ? "Bem-vindo de volta à VYRA" 
              : "Comece sua jornada de acolhimento"}
          </p>
        </div>

        <form onSubmit={handleAuth} className="space-y-4">
          {!isLogin && (
            <div>
              <Label htmlFor="name">Nome (opcional)</Label>
              <Input
                id="name"
                type="text"
                placeholder="Como prefere ser chamado?"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1"
              />
            </div>
          )}

          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="seu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="password">Senha</Label>
            <div className="relative mt-1">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          <Button type="submit" className="w-full" disabled={loading}>
            {loading 
              ? "Aguarde..." 
              : isLogin ? "Entrar" : "Criar conta"}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => setIsLogin(!isLogin)}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {isLogin 
              ? "Não tem conta? Criar uma agora" 
              : "Já tem conta? Fazer login"}
          </button>
        </div>
      </Card>
    </div>
  );
};

export default Login;